import { useState, useCallback, ChangeEventHandler, useEffect, useMemo, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Dropdown } from 'components/Dropdown';
import { DropdownOption } from 'components/Dropdown/components/DropdownItem';
import { PopoverOption } from 'components/Popover/components/PopoverItem';
import { Loader } from 'components/Loader';

import {
  getArtworks as getArtworksAction,
  sortArtworks as sortArtworksAction,
  searchArtwork as searchArtworkAction,
  resetArtworksFilters as resetArtworksFiltersAction,
} from 'modules/artworks/actions';
import {
  setArtwork as setArtworkAction,
  resetArtwork as resetArtworkAction,
} from 'modules/artwork/actions';

import { useAction, getCompanyIdAndCategory, useScrolledToBottom } from 'utils';
import { Store, ArtworkOrdering, Artwork } from 'types';

import { LoadingBlock } from './components/LoadingBlock';
import { ArtworkCard } from './components/ArtworkCard';
import { CreateArtworkModal } from './components/CreateArtworkModal';
import { ArtworkModal } from './components/ArtworkModal';
import { DeleteArtworkModal } from './components/DeleteArtworkModal';

import styles from './styles.module.scss';

const sortOptions: DropdownOption<ArtworkOrdering>[] = [
  { label: 'Sort by date created', value: 'created' },
  { label: 'Sort by name', value: 'name' },
  { label: 'Sort by type', value: 'type' },
];

export const Artworks: React.FC<RouteComponentProps> = ({ location }) => {
  const [searchValue, setSearchValue] = useState('');
  const searchTimoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedSortOption, setSelectedSortOption] = useState(sortOptions[0]);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledToBottom = useScrolledToBottom(containerRef);
  const { loading, loadingMore, companies } = useSelector((store: Store) => store.artworks);
  const getArtworks = useAction(getArtworksAction.request);
  const sortArtworks = useAction(sortArtworksAction.request);
  const searchArtwork = useAction(searchArtworkAction.request);
  const resetArtworksFilters = useAction(resetArtworksFiltersAction);
  const [newArtworkModalOpened, setNewArtworkModalOpened] = useState(false);
  const [artworkModalOpened, setArtworkModalOpened] = useState(false);
  const [deleteArtworkModalOpened, setDeleteArtworkModalOpened] = useState(false);
  const setArtwork = useAction(setArtworkAction);
  const resetArtwork = useAction(resetArtworkAction);

  const companyIdAndCategory = useMemo(() => {
    return getCompanyIdAndCategory(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!companies[companyIdAndCategory!.companyId]) {
      getArtworks({
        companyId: companyIdAndCategory!.companyId,
        search: '',
        limit: 10,
        offset: 0,
        ordering: selectedSortOption.value,
      });
    }
  }, [companies, companyIdAndCategory, getArtworks, selectedSortOption]);

  useEffect(() => {
    const companyArtworksResponse = companies[companyIdAndCategory!.companyId];

    if (
      scrolledToBottom &&
      companyArtworksResponse.results.length < companyArtworksResponse.count
    ) {
      getArtworks({
        companyId: companyIdAndCategory!.companyId,
        search: searchValue,
        limit: 10,
        offset: companyArtworksResponse.results.length,
        ordering: selectedSortOption.value,
      });
    }
  }, [
    companies,
    companyIdAndCategory,
    getArtworks,
    scrolledToBottom,
    searchValue,
    selectedSortOption.value,
  ]);

  useEffect(() => {
    searchTimoutRef.current = setTimeout(() => {
      searchArtwork({
        companyId: companyIdAndCategory!.companyId,
        search: searchValue,
        limit: 10,
        offset: 0,
        ordering: selectedSortOption.value,
      });
    }, 500);

    return () => {
      clearTimeout(searchTimoutRef.current!);
    };
  }, [companyIdAndCategory, searchArtwork, searchValue, selectedSortOption.value]);

  useEffect(() => {
    return () => {
      resetArtworksFilters(companyIdAndCategory!.companyId);
    };
  }, [companyIdAndCategory, resetArtworksFilters]);

  const handleSearchValueChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const handleSortOptionChange = useCallback(
    (selectedOption: DropdownOption) => {
      sortArtworks({
        companyId: companyIdAndCategory!.companyId,
        search: searchValue,
        limit: 10,
        offset: 0,
        ordering: selectedOption.value,
      });

      setSelectedSortOption(selectedOption);
    },
    [companyIdAndCategory, searchValue, sortArtworks],
  );

  const handleCreateNewArtworkClick = useCallback(() => {
    setNewArtworkModalOpened(true);
  }, []);

  const handleCreateArtworkModalClose = useCallback(() => {
    setNewArtworkModalOpened(false);
    resetArtwork();
  }, [resetArtwork]);

  const handleCreateArtworkSuccess = useCallback(() => {
    setNewArtworkModalOpened(false);
    setArtworkModalOpened(true);
  }, []);

  const handleUpdateArtworkSuccess = useCallback(() => {
    setNewArtworkModalOpened(false);
    resetArtwork();
  }, [resetArtwork]);

  const handleArtworkModalClose = useCallback(() => {
    setArtworkModalOpened(false);
    resetArtwork();
  }, [resetArtwork]);

  const handleArtworkClick = useCallback(
    (artwork: Artwork) => {
      setArtwork(artwork, false);
      setArtworkModalOpened(true);
    },
    [setArtwork],
  );

  const handleArtworkActionClick = useCallback(
    (artwork: Artwork, selectedAction: PopoverOption) => {
      setArtwork(artwork, false);
      if (selectedAction.label === 'Delete') {
        setDeleteArtworkModalOpened(true);
      } else {
        setNewArtworkModalOpened(true);
      }
    },
    [setArtwork],
  );

  const handleDeleteArtworkModalOpen = useCallback(() => {
    setDeleteArtworkModalOpened(true);
  }, []);

  const handleDeleteArtworkModalClose = useCallback(() => {
    setDeleteArtworkModalOpened(false);
    if (!artworkModalOpened) {
      resetArtwork();
    }
  }, [artworkModalOpened, resetArtwork]);

  const handleArtworkDeleteSuccess = useCallback(() => {
    setDeleteArtworkModalOpened(false);
    if (artworkModalOpened) {
      setArtworkModalOpened(false);
    }
    resetArtwork();
  }, [artworkModalOpened, resetArtwork]);

  const handleEditArtwork = useCallback(() => {
    setArtworkModalOpened(false);
    setNewArtworkModalOpened(true);
  }, []);

  const loadingBlocks = useMemo(() => {
    return (
      <>
        {new Array(15)
          .fill(undefined)
          .map(() => Math.random())
          .map((randomNumber) => (
            <LoadingBlock key={randomNumber} />
          ))}
      </>
    );
  }, []);

  const artworks = useMemo(() => {
    if (companies[companyIdAndCategory!.companyId]) {
      return (
        <>
          {companies[companyIdAndCategory!.companyId].results.map((artwork) => (
            <ArtworkCard
              key={artwork.id}
              data={artwork}
              onActionClick={handleArtworkActionClick}
              onClick={handleArtworkClick}
            />
          ))}
        </>
      );
    }

    return null;
  }, [companies, companyIdAndCategory, handleArtworkActionClick, handleArtworkClick]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.heading_container}>
        <h1>Artworks</h1>
        <Button
          showPlusIcon
          className={styles.new_artwork_button}
          title="New artwork"
          onClick={handleCreateNewArtworkClick}
        />
      </div>
      <div className={styles.filters_container}>
        <Input
          searchInput
          name="search"
          placeholder="Search artworks"
          className={styles.search_input_container}
          value={searchValue}
          onChange={handleSearchValueChange}
        />
        <Dropdown
          name="sort"
          selectedOption={selectedSortOption}
          options={sortOptions}
          onChange={handleSortOptionChange}
        />
      </div>
      <div className={styles.artworks_container}>{loading ? loadingBlocks : artworks}</div>
      <div className={styles.loader_container}>
        <Loader loading={loadingMore} />
      </div>
      {newArtworkModalOpened && (
        <CreateArtworkModal
          companyId={companyIdAndCategory!.companyId}
          onClose={handleCreateArtworkModalClose}
          onCreateArtwork={handleCreateArtworkSuccess}
          onUpdateArtwork={handleUpdateArtworkSuccess}
        />
      )}
      {artworkModalOpened && (
        <ArtworkModal
          onClose={handleArtworkModalClose}
          onDelete={handleDeleteArtworkModalOpen}
          onEdit={handleEditArtwork}
        />
      )}
      {deleteArtworkModalOpened && (
        <DeleteArtworkModal
          companyId={companyIdAndCategory!.companyId}
          onClose={handleDeleteArtworkModalClose}
          onArtworkDeleteSuccess={handleArtworkDeleteSuccess}
        />
      )}
    </div>
  );
};
